<template>
  <section style="position: relative;">
    <v-card class="pa-4 mb-4">
      <!-- header filters -->
      <v-row>
        <v-col>
          <h5 class="grey--text">
            {{ t.filterByAnalysisStatus }}
          </h5>
        </v-col>
      </v-row>

      <v-row style="flex-wrap: nowrap;">
        <v-col class="flex-grow-1 pl-4">
          <v-checkbox
            v-for="option of analysisOptions"
            :key="option.key"
            v-model="option.val"
            :label="`${option.name} (${occurences[option.key] || 0})`"
            hide-details
            class="mr-5 d-inline-block"
          />
        </v-col>
        <v-col class="flex-grow-1">
          <v-text-field
            v-model="searchTerm"
            :label="t.filterByTitleOfSummary"
            outlined
            class="mr-5"
          />
        </v-col>
        <v-col class="flex-grow-0">
          <v-btn
            color="primary"
            class="mt-3"
            @click="resetFilters"
          >
            {{ t.resetFilters }}
          </v-btn>
        </v-col>
      </v-row>
    </v-card>

    <div
      v-for="category of viewableCategories"
      :key="category"
      class="mb-6"
    >
      <h3 class="mb-3">
        {{ category }}
      </h3>
      <template
        v-for="(section, index) of longTermOptions[category]"
      >
        <v-sheet
          v-if="shouldShowByStatus(section)"
          :key="`${category}-${index}`"
          class="pr-2 mb-4"
          elevation="1"
          rounded
          :class="section.locked ? 'light-green' : 'amber'"
        >
          <v-card
            elevation="0"
            tile
          >
            <v-card-title class="d-flex justify-space-between align-center">
              <div>
                <h5>
                  {{ section.shortTitle }}
                  <v-tooltip
                    color="primary"
                    bottom
                    max-width="500"
                  >
                    <template #activator="{ on, attrs }">
                      <sup>
                        <v-icon
                          color="primary"
                          small
                          v-bind="attrs"
                          v-on="on"
                        >
                          help
                        </v-icon>
                      </sup>
                    </template>
                    <span>
                      {{ section.summary }}
                    </span>
                  </v-tooltip>
                </h5>
                <div
                  v-if="section.legalReference"
                  class="text-caption grey--text mt-1"
                >
                  {{ section.legalReference }}
                  <a
                    v-if="section.link"
                    :href="section.link"
                    target="_new"
                  >
                    <v-icon x-small>mdi-arrow-top-right</v-icon>
                  </a>
                </div>
              </div>
              <div class="d-flex align-center">
                <v-chip
                  class="mr-2"
                  label
                  :color="getSectionStatus(section) == 'no-analysis' ? 'white' : getSectionStatus(section).color"
                >
                  {{ getSectionStatus(section).name }}
                </v-chip>
                <v-btn
                  icon
                  @click="toggleCollapsed(`${category}-${index}`)"
                >
                  <v-icon>
                    {{ collapsedSections[`${category}-${index}`] ? 'unfold_more' : 'unfold_less' }}
                  </v-icon>
                </v-btn>
                <v-tooltip
                  v-if="peUserCanUpdate"
                  top
                >
                  <template #activator="{ on, attrs }">
                    <v-btn
                      icon
                      small
                      :color="section.locked ? 'primary' : 'success'"
                      v-bind="attrs"
                      v-on="on"
                      @click="section.locked ? switchEditing(section) : onSaveSection(section)"
                    >
                      <v-icon>{{ section.locked ? 'mdi-pencil' : 'mdi-content-save' }}</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ section.locked ? t.edit : t.lock }}</span>
                </v-tooltip>
              </div>
            </v-card-title>
            <v-expand-transition>
              <v-card-text v-show="!collapsedSections[`${category}-${index}`]">
                <v-radio-group
                  v-model="section.status"
                  :disabled="section.locked || !peUserCanUpdate"
                  row
                  class="mt-0"
                >
                  <v-radio
                    v-for="option of analysisOptions"
                    :key="`${option.name}-${option.key}`"
                    :label="option.name"
                    :value="option.key"
                  />
                </v-radio-group>
                <v-textarea
                  v-model="section.narrative"
                  auto-grow
                  outlined
                  :label="t.narrative"
                  :disabled="section.locked || !peUserCanUpdate"
                />
              </v-card-text>
            </v-expand-transition>
          </v-card>
        </v-sheet>
      </template>
    </div>

    <v-overlay
      absolute
      :value="!canView"
      :class="{'party-overlay': !canView}"
    >
      <v-row
        align="start"
        justify="center"
      >
        <v-col
          cols="12"
          class="text-center"
        >
          <h2>You do not have permissions to view this section.</h2>
        </v-col>
      </v-row>
    </v-overlay>
  </section>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { flatten } from 'lodash'

import Actions from '@/data/Permissions/enums.json'
import { alertHandler } from '@/alertHandler'

import { LONG_TERM_OPTIONS_NAMESPACE } from '@/store/long-term-options'
import { PARTY_NAMESPACE } from '@/store/party'
import { USER_NAMESPACE } from '@/store/user'

export default {
  name: 'LongTermOptionsTab',

  beforeRouteLeave (_, __, next) {
    if (this.hasUnsavedChanges) {
      const answer = window.confirm('Do you really want to leave? you have unsaved changes!')
      if (answer) next()
      else next(false)
    } else {
      next()
    }
  },

  props: {
    id: {
      type: String,
      required: true,
    },
  },

  setup () {
    return { ...alertHandler() }
  },

  data () {
    return {
      canView: true,
      collapsedSections: {},
      infoPanel: {
        eligibility: '',
        ineligibility: '',
        show: false,
        title: '',
      },

      searchTerm: '',
      t: {
        analysisStatus: 'Analysis Status',
        edit: 'Edit',
        filterByAnalysisStatus: 'Filter by Analysis Status',
        filterByTitleOfSummary: 'Filter by Title or Summary',
        lock: 'Save',
        narrative: 'Narrative',
        resetFilters: 'Reset',
        shortTitle: 'Short Title',
        statutoryCitation: 'Statutory Citation',
      },
    }
  },

  computed: {
    hasUnsavedChanges () {
      return flatten(Object.values(this.longTermOptions)).some(section => !section.locked)
    },

    occurences () {
      return flatten(Object.values(this.longTermOptions)).reduce((p, c) => {
        const status = c.status || 'no-analysis'
        if (!p[status]) p[status] = 0
        p[status]++
        return p
      }, {})
    },

    partyId () { return this.id },

    statusFilter () {
      return this.analysisOptions.reduce((p, c) => {
        p[c.key] = c.val || false
        return p
      }, {})
    },

    userCan () {
      return {
        viewSection: this.can(Actions.Platform.PartyEdit.Sections.LongTermOptions.View),
      }
    },

    viewableCategories () {
      return Object.keys(this.longTermOptions).filter(category => this.shouldShowCategory(category))
    },

    ...mapGetters(LONG_TERM_OPTIONS_NAMESPACE, [
      'analysisOptions',
      'longTermOptions',
    ]),

    ...mapGetters(PARTY_NAMESPACE, [
      'peUserCanUpdate',
    ]),

    ...mapGetters(USER_NAMESPACE, [
      'can',
    ]),
  },

  async created () {
    if (!this.userCan.viewSection) {
      this.canView = false
      this.displayAlert('error', 'You do not have permissions to view this section.')
      return
    }

    await this.peGetSelectedPartySection({ id: this.partyId, section: 'long_term_options' }).
      catch(e => {
        if (e.status === 403) this.$router.push({ name: 'resourceAccessDenied', query: { party_id: this.id } })
        else if (e.status === 404) this.$router.push({ name: 'resourceNotFound' })
        else this.displayAlert('error', `An unexpected error occurred: ${e.message}`)
      })

    await this.getLongTermOptions({ partyId: this.partyId })
  },

  methods: {
    getSectionStatus (section) {
      const status = section.status || 'no-analysis'
      return this.analysisOptions.find(option => option.key === status)
    },

    onSaveSection (section) {
      if (!section.status) {
        this.displayAlert('error', 'Please select a status before saving')
        return
      }
      section.locked = true
      this.saveLongTermOption({ partyId: this.partyId, section }).
        then(_ => this.displayAlert('success', 'Long Term Option saved successfully')).
        catch(_ => this.displayAlert('error', 'error saving Long term Option'))
    },

    resetFilters () {
      this.searchTerm = ''
      for (const option of this.analysisOptions) option.val = false
    },

    shouldShowBySearchTerm (section) {
      const sectionHash = Object.values(section).join()
      return sectionHash.includes(this.searchTerm)
    },

    shouldShowByStatus (section) {
      if (!this.analysisOptions.find(o => o.val)) return this.shouldShowBySearchTerm(section)
      return this.statusFilter[section.status || 'no-analysis'] && this.shouldShowBySearchTerm(section)
    },

    shouldShowCategory (category) {
      return this.longTermOptions[category]?.find(o => this.shouldShowByStatus(o))
    },

    showInfo (section) {
      this.infoPanel.title = section.short_title
      this.infoPanel.eligibility = section.eligibility
      this.infoPanel.ineligibility = section.ineligibility

      this.infoPanel.show = true
    },

    switchEditing (section) {
      section.locked = false
      this.saveLongTermOption({ partyId: this.partyId, section }).
        then(_ => this.displayAlert('success', 'Long Term Option unlocked successfully')).
        catch(_ => this.displayAlert('error', 'error unlocking Long term Option'))
    },

    toggleCollapsed (key) {
      this.$set(this.collapsedSections, key, !this.collapsedSections[key])
    },

    ...mapActions(LONG_TERM_OPTIONS_NAMESPACE, [
      'getLongTermOptions',
      'saveLongTermOption',
    ]),

    ...mapActions(PARTY_NAMESPACE, [
      'peGetSelectedPartySection',
    ]),
  },
}
</script>

<style scoped>
.party-overlay {
  margin-top: -75px;
  padding-top: 50vh;
  z-index: 10;
  backdrop-filter: blur(6px);
}
</style>
